<template>
  <div>
    <div class="content">
      <div v-show="!textPreview" id="email-builder" class="builder"></div>
      <email-text-preview
        v-show="textPreview"
        ref="textPreview"
        :on-close="closeTextPreview"
      />
    </div>
    <div class="overlay"></div>
    <div class="control-bar">
      <div class="control-buttons">
        <div
          v-if="
            $can('updateProjectItem') || $can('CampaignTemplate:FullAccess')
          "
          class="control-button"
          @click="save"
        >
          <i class="icon-check" />
          Save
        </div>
        <div class="control-button" @click="preview">
          <i class="icon-eye" />
          Preview
        </div>
        <div class="control-button" @click="openTextPreview">
          <i class="icon-doc-text" />
          Text Preview
        </div>
        <div class="control-button" @click="sendTest">
          <i class="icon-doc-text" />
          Test Send
        </div>
        <div class="control-button" @click="confirmClose">
          <i class="icon-cancel" />
          Discard
        </div>
      </div>
      <div class="launcher">
        <h1 class="launcher-button">
          <el-button
            icon="el-icon-question"
            type="primary"
            @click="showZendeskWidget"
            round
          >
            Help
          </el-button>
        </h1>
      </div>
    </div>
    <vuedal></vuedal>
  </div>
</template>

<script>
import Bee from 'bee-plugin'
import BeeAuth from '@/common/components/email/BeeAuth'
import BlankTemplate from '@/common/components/email/BlankTemplate'
import EmailTextPreview from '@/common/components/email/EmailTextPreview'
import { Component as Vuedal } from 'vuedals'
import RestApi from '@/core/services/RestApi'
import { MessageBox } from 'element-ui'
import { projectEndpoint } from '@/core/helpers'
import { $hasFeature } from '@/core/utils/VueAcl'
import { BeeLanguageEnum } from '@/common/enum/BeeLanguageEnum'

export default {
  name: 'EmailBuilder',
  components: { EmailTextPreview, Vuedal },
  props: {
    template: [Object, String],
    onSave: Function,
    onError: Function,
    onClose: Function,
    onConfirmClose: Function,
    onSendTest: Function,
    generateTextPreview: Function,
    flags: Object
  },

  data: () => {
    return {
      textPreview: false,
      sendingTest: false,
      BeeLanguageEnum
    }
  },

  created() {
    if (
      'xp' in window &&
      window.xp.project.id &&
      typeof localStorage !== 'undefined'
    ) {
      localStorage.setItem('xp-project-id', window.xp.project.id)
      localStorage.setItem('xp-access-token', window.xp.accessToken)
    }
    this.loadBuilder()
  },

  beforeDestroy() {
    const el = document.getElementById('email-builder')
    if (el) {
      el.parentElement.removeChild(el)
    }
  },

  computed: {
    beeLanguage() {
      let store = this.$store || window.vue.$store
      if (!store) {
        return BeeLanguageEnum['en']
      }

      let language = BeeLanguageEnum[store.state.app.user.language]
      if (!language) {
        language = BeeLanguageEnum[store.state.app.provider.language]
      }
      return language || BeeLanguageEnum['en']
    }
  },

  methods: {
    async loadBuilder() {
      const specialLinks = [
        {
          type: 'links',
          label: 'Unsubscribe',
          link: '{{ system.url.unsubscribe }}'
        }
      ]
      if (this.flags && this.flags.consentPreference) {
        specialLinks.push({
          type: 'links',
          label: 'Unsubscribe from all',

          link: '{{ system.url.unsubscribeall }}'
        })
      }
      specialLinks.push({
        type: 'links',
        label: 'View Online',
        link: '{{ system.url.archive }}'
      })

      let projectId = window.xp.project.id

      let shopboxPluginAllowed = false
      let voucherCodesAllowed = false

      try {
        const result = await RestApi.get(`projects/${projectId}/config`)

        if (result.data) {
          shopboxPluginAllowed =
            (result.data.features && result.data.features.shopboxEnabled) ||
            false
          voucherCodesAllowed =
            (result.data.features &&
              result.data.features.allowVoucherCodes &&
              this.flags &&
              this.flags.voucherCodesAllowed) ||
            false
        }
      } catch (_) {
        shopboxPluginAllowed = false
      }

      let attributes = await RestApi(projectEndpoint({ url: 'attributes' }))
      const mergeTags = attributes.data.map(record => {
        let path
        if (
          !$hasFeature('twig') ||
          record.name.match('^[a-zA-Z]+[a-zA-Z0-9_.]{0,}$')
        ) {
          path = record.name
        } else {
          path = "user.attributes['" + record.name.replaceAll(`'`, `\\'`) + "']"
        }

        return {
          name: record.name,
          value: `{{ ${path} }}`
        }
      })

      let fonts = await RestApi(
        projectEndpoint({ method: 'get', url: 'web-font' })
      )
      let customFonts = fonts.data.map(record => ({
        name: record.name,
        fontFamily: record.family,
        url: record.url
      }))

      const that = this

      let config = {
        container: 'email-builder',
        language: this.beeLanguage,
        roleHash: 'PowerUser',
        trackChanges: true,
        saveRows: true,
        editorFonts: {
          showDefaultFonts: true,
          customFonts: customFonts
        },
        specialLinks,
        mergeTags,
        addOns: [
          {
            id: 'product-recommendations-dialog',
            enabled: shopboxPluginAllowed
          },
          {
            id: 'ai-integration',
            enabled: $hasFeature('aiTextGeneration')
          },
          {
            id: 'voucher-codes-addon',
            enabled: voucherCodesAllowed
          }
        ],
        rowsConfiguration: {
          emptyRows: true,
          defaultRows: false,
          externalContentURLs: [
            {
              name: 'Saved',
              value: new URL(
                '/builder/saved-rows?project_id=' + projectId,
                window.location.href
              ).href
            }
          ]
        },

        onSave: (jsonFile, htmlFile) => {
          if (this.textPreview) {
            this.updateTextPreview(htmlFile)
          } else if (this.sendingTest && this.onSendTest) {
            this.sendingTest = false
            return this.onSendTest(htmlFile)
          } else if (this.onSave) {
            this.onSave(jsonFile, htmlFile)
          }
          this.sendingTest = false
        },
        onError: errorMessage => {
          // eslint-disable-next-line no-console
          console.log('BeeError ', errorMessage)
          if (this.onError) {
            this.onError(errorMessage)
          }
        },
        onChange: () => {
          this.isChanged = true
        },

        onSaveRow: function(rowJSON, rowHTML, pageJSON) {
          RestApi.post('projects/' + projectId + '/content', {
            type: 1,
            content: rowHTML,
            meta: JSON.stringify({ rowJSON: JSON.parse(rowJSON), pageJSON })
          })
        },
        contentDialog: {
          saveRow: {
            handler: async function(resolve, reject) {
              try {
                let response = await MessageBox.prompt(null, 'Save Row', {
                  confirmButtonText: 'Save',
                  cancelButtonText: 'Cancel',
                  inputPlaceholder: 'Enter name'
                })
                resolve({ name: response.value })
              } catch (error) {
                reject()
              }
            }
          },
          addOn: {
            handler: function(resolve, reject, args) {
              const { contentDialogId, value } = args

              if (contentDialogId === 'product-recommendations-dialog') {
                that.createBeePluginWindow(
                  resolve,
                  reject,
                  value,
                  'bee-shopbox-plugin'
                )
              } else if (contentDialogId === 'voucher-codes-addon') {
                that.createBeePluginWindow(
                  resolve,
                  reject,
                  value,
                  'bee-voucher-plugin'
                )
              } else {
                reject('Unknown plugin')
              }
            }
          }
        }
      }

      this.textPreview = false

      let template = this.template
      if (!template) {
        template = BlankTemplate
      }

      BeeAuth.getToken().then(token => {
        config.uid = token.uid
        config.roleHash = token.roleHash
        this.bee = new Bee(token)
        this.bee.start(config, template)
      })
    },

    save() {
      this.textPreview = false
      this.bee.save()
    },

    preview() {
      this.textPreview = false
      this.bee.preview()
    },

    openTextPreview() {
      this.$refs.textPreview.text = ''
      this.$refs.textPreview.loading = true
      this.textPreview = true
      this.bee.save()
    },

    updateTextPreview(html) {
      this.generateTextPreview(html, text => {
        this.$refs.textPreview.text = text
        this.$refs.textPreview.loading = false
      })
    },

    closeTextPreview() {
      this.textPreview = false
    },

    sendTest() {
      this.sendingTest = true
      this.bee.save()
    },

    confirmClose() {
      if (this.isChanged) {
        this.onConfirmClose()
      } else {
        this.close()
      }
    },

    close() {
      if (this.onClose) {
        this.onClose()
      }
    },

    showZendeskWidget() {
      this.$zendesk.show()
      this.$zendesk.open()
    },

    createBeePluginWindow(resolve, reject, value, componentName) {
      this.$vuedals.open({
        name: componentName + '-modal',

        props: {
          resolve: resolve,
          reject: reject,
          'current-state': this.decodeValue(value)
        },

        component: componentName,

        dismissable: false,

        closeOnBackdrop: false
      })
    },

    decodeValue(value) {
      if (!value.hasOwnProperty('html')) {
        return {}
      }

      const data = value.html
      try {
        const parser = new DOMParser()
        this.parsedData = JSON.parse(
          decodeURIComponent(
            parser
              .parseFromString(data, 'text/html')
              .documentElement.querySelector('div').dataset.pluginState
          )
        )
        return this.parsedData
      } catch (e) {
        // parsing exception
        return {}
      }
    }
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  z-index: 1000000;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: #32373a;
}
.content {
  position: fixed;
  z-index: 1000001;
  top: 55px;
  left: 0;
  right: 0;
  bottom: 0;
}
.builder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.control-bar {
  position: fixed;
  z-index: 1000002;
  top: 0;
  left: 0;
  right: 0;
  height: 55px;
  background: #505659;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.control-buttons {
  float: left;
}
.control-button {
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  border-radius: 4px;
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  float: left;
  height: 55px;
  line-height: 55px;
  font-size: 14px;
  color: #ffffff;
  padding: 0 5px;
  margin-right: 20px;
}
.control-button:hover {
  opacity: 0.8;
}
.launcher {
  display: flex;
  align-items: center;
}
h1.launcher-button {
  display: inline-block;
}

>>> .vuedals {
  z-index: 10000000;
}

>>> .vuedal {
  max-height: 85vh;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/common/components/email/EmailBuilder.vue